/**
 * 子话题趋势图表组件 - 折线图/柱状图
 * 遵循 Opinion 图表开发规范
 */
import React, { useContext, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import { DBO_V, DBO_EC } from '@op/utils/format';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { chartAllColors } from '@op/constants';
import useLanguage from '@op/hooks/useLanguage';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { ClassSpecial } from '@op/constants/common';
import { SecurityWeeklyReportContext } from '@op/pages/SecurityWeeklyReport/context';

type TChartType = 'line' | 'bar';

interface ISubTopicTrendChartProps {
  data?: TRequestBackType;
  chartType: TChartType;
  height: number;
  filter?: any;
  selectedType?: string | null;
  onChartClick?: (params: any) => void;
  getDisplayName: (value: string) => string;
}

const SubTopicTrendChart: React.FC<ISubTopicTrendChartProps> = ({
  data,
  chartType,
  height = 350,
  filter,
  selectedType,
  onChartClick,
  getDisplayName,
}) => {
  const { formatMessage } = useLanguage();
  const { toFeeds } = useContext(SecurityWeeklyReportContext);

  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: () => toFeeds(),
  });

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    // 通用配置
    const commonConfig = {
      color: chartAllColors,
      grid: { top: 20 },
      tooltip: {
        trigger: 'axis' as const,
        className: `${ClassSpecial.CHART_TOOLTIP_SCROLL} -300`,
        enterable: true,
        confine: false,
        position: (point: any, params: any, dom: any, rect: any, size: any) =>
          DBO_EC.enterablePosition(point, params, dom, rect, size, {
            verticalOffset: 30,
          }),

        formatter: (params: any) => {
          const time = params[0]?.axisValue;
          const title = DBO_EC.toolTime(time, filter?.date_type || 'daily');
          return formatChartParamsDate({
            params,
            isSort: true,
            valueFormat: (value: number) => DBO_V.reviews(value),
            tablesConfigFunc: (records: any) => [
              {
                showHeader: true,
                records,
                columns: [
                  { key: 'text', header: title },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                ],
              },
            ],
            textShowFun: (marker: string, seriesName: string) => {
              return `<span class='echart-tooltip'><span class="chart-common-formatter-marker">${marker}</span>&nbsp;${getDisplayName(
                seriesName,
              )}</span>`;
            },
            footerText: getBottomClickBtn({
              StartTime: params[0]?.axisValue || '',
              EndTime: params[0]?.axisValue || '',
            }),
          });
        },
      },
      legend: {
        data: data.legend,
        bottom: 0,
        type: 'scroll' as const,
        formatter: (name: string) => getDisplayName(name),
      },
      xAxis: {
        type: 'category' as const,
        data: data.xAxis,
        axisTick: { show: false },
        axisLabel: {
          formatter: (v: string) => DBO_EC.timeX(v, filter.date_type),
        },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: { formatter: DBO_EC.reviews },
        axisLine: { show: false },
      },
    };

    // 柱状图
    if (chartType === 'bar') {
      return {
        ...commonConfig,
        series:
          data.series?.map((item, index) => {
            const isSelected = selectedType === item.name;
            const baseColor = chartAllColors[index % chartAllColors.length];
            const selectedBorderColor = '#D4A500';
            return {
              name: item.name,
              type: 'bar' as const,
              data: item.data,
              stack: 'total',
              itemStyle: {
                color:
                  selectedType && !isSelected ? `${baseColor}40` : baseColor,
                borderColor: isSelected ? selectedBorderColor : 'transparent',
                borderWidth: isSelected ? 2 : 0,
              },
              emphasis: {
                itemStyle: {
                  color: baseColor,
                  borderColor: selectedBorderColor,
                  borderWidth: 2,
                },
              },
            };
          }) || [],
      };
    }

    // 折线图（默认）- 选中线高亮，其他变淡
    return {
      ...commonConfig,
      series:
        data.series?.map((item, index) => {
          const isSelected = selectedType === item.name;
          const baseColor = chartAllColors[index % chartAllColors.length];
          const displayColor =
            selectedType && !isSelected ? `${baseColor}40` : baseColor;
          const lineWidth = isSelected ? 3 : 2;
          return {
            name: item.name,
            type: 'line' as const,
            data: item.data,
            showSymbol: isSelected,
            lineStyle: {
              width: lineWidth,
              color: displayColor,
            },
            itemStyle: {
              color: displayColor,
            },
            z: isSelected ? 10 : 1,
          };
        }) || [],
    };
  }, [
    data,
    chartType,
    filter,
    selectedType,
    getDisplayName,
    getBottomClickBtn,
    formatMessage,
  ]);

  const eventsDict = useMemo(() => {
    const events: Record<string, Function> = {};
    if (onChartClick) {
      events.click = onChartClick;
    }
    return events;
  }, [onChartClick]);

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon
        options={option}
        height={height}
        empty={data?.isEmpty}
        optionsProps={{ type: chartType }}
        eventsDict={eventsDict}
      />
    </div>
  );
};

export default SubTopicTrendChart;
